import React, { Fragment } from 'react';
import PropTypes from 'prop-types';
import { get } from 'lodash-es';
import classnames from 'classnames';
import { Row, Col } from 'reactstrap';
import { connect } from 'react-redux';
/* Models */
import { InventoryEntities } from 'client/data/models/inventory';
import { DealerInfoEntities } from 'client/data/models/dealer-info';
/* Constants */
import { ACTIONS } from 'client/engagement-handlers/inventory-engagement-handler/constants';
import { RETAILER_OFFERS } from 'site-modules/shared/constants/inventory/buy-online-dealers';
/* Utils */
import {
  getDealerLinkWithUTMParams,
  getCommonDeepLink,
} from 'client/site-modules/shared/utils/dealership-link-constructor';
import { getStaticImageUrl } from 'client/utils/image-helpers';
import { EventToolbox } from 'client/utils/event-toolbox';
import { isCarMax } from 'site-modules/shared/utils/dealer-details-utils';
import { VdpModule } from 'site-modules/shared/components/inventory/vdp-module/vdp-module';

export const MidPageWidgetViewUI = ({
  vinData = {},
  zipCode,
  dealerInfo = null,
  isMobile = false,
  isProdEnv = false,
}) => {
  const type = get(vinData, 'type', '');
  const dealer = get(vinData, 'dealerInfo.displayInfo.parentDealershipName');
  const offersDisaplayConfig = RETAILER_OFFERS[dealer];
  const inventoryType = type.toLowerCase();
  const BUTTON_COPY = isCarMax(dealer) ? offersDisaplayConfig.ctaTitle : 'Get It Delivered';

  const handleBtnClick = () => {
    EventToolbox.fireCustomEvent(ACTIONS.EMBEDDED_BOX_BTN_CLICK, {
      partnerId: get(vinData, 'dealerInfo.rooftopId'),
      type: type.toLowerCase(),
      value: BUTTON_COPY,
    });
  };

  const url = getDealerLinkWithUTMParams({
    vinData,
    dealershipInfo: dealerInfo,
    zipCode,
    dealer,
    isProdEnv,
    isExtended: true,
    getDeepLink: getCommonDeepLink,
  });

  const separator = !isMobile && <div className="separator my-auto w-auto" />;

  if (!offersDisaplayConfig) return null;

  return (
    <VdpModule
      className="bg-cool-gray-90 mb-1_5"
      indentationSize={1.5}
      data-tracking-parent={`${inventoryType}-buy_online_cta_embedded-${inventoryType}_model_car_inventory_vin_detail`}
    >
      <div className="d-flex align-items-center mb-1_5">
        <div className="d-flex flex-wrap align-items-center">
          <span className={classnames('fw-bold', { 'size-16': isMobile, 'size-20': !isMobile })}>
            Buy Online from&nbsp;
          </span>
          <img
            className="d-inline-block mw-100"
            src={getStaticImageUrl(offersDisaplayConfig.logo)}
            alt={`${dealer} Logo`}
            loading="lazy"
            width={offersDisaplayConfig?.logoWidth ?? 122}
          />
        </div>
        <a
          href={url}
          target="_blank"
          rel="noopener noreferrer"
          className="btn text-capitalize text-white btn-primary-b px-1_5 size-16 ms-0 ms-md-1_5"
          onClick={handleBtnClick}
          data-test="get_it_delivery_secondary"
        >
          <span className="align-self-center fw-normal">
            {BUTTON_COPY}
            <i className="icon-open_in_new medium ms-0_25" aria-hidden />
          </span>
        </a>
      </div>
      <Row className="list-unstyled mb-0" tag="ul">
        {offersDisaplayConfig.copy.map(({ text, icon, iconImg, className }, index) => (
          <Fragment key={`icon_${index + 1}`}>
            {index === 1 && separator}
            <Col className="text-center" {...(isMobile && { xs: 12 })} tag="li">
              <div className={classnames('mb-md-0', { 'd-flex align-items-center': isMobile })}>
                {iconImg && (
                  <div className="icon-img me-1 me-md-0" style={{ minHeight: '26px' }}>
                    <img
                      className={classnames('d-block mx-md-auto mw-100', className)}
                      src={getStaticImageUrl(iconImg)}
                      alt=""
                      loading="lazy"
                      width={24}
                    />
                  </div>
                )}
                {icon && (
                  <div className="icon-img me-1 me-md-0">
                    <i className={`${icon} text-primary size-24`} aria-hidden />
                  </div>
                )}
                <div className="mt-md-0_25 px-md-0_25 d-inline-block text-cool-gray-40">{text}</div>
              </div>
            </Col>
            {index === 1 && separator}
          </Fragment>
        ))}
      </Row>
    </VdpModule>
  );
};

MidPageWidgetViewUI.propTypes = {
  vinData: InventoryEntities.InventoryVin,
  isMobile: PropTypes.bool.isRequired,
  zipCode: PropTypes.string.isRequired,
  dealerInfo: DealerInfoEntities.DealerInfo,
  isProdEnv: PropTypes.bool,
};

export const mapStateToProps = state => ({
  isProdEnv: get(state, 'venomEnv') === 'prod',
});

export const MidPageWidgetView = connect(mapStateToProps)(MidPageWidgetViewUI);
